import { defineStore } from 'pinia'

import * as salesQuarterApi from '../services/api/sales-quarter'

export interface ISalesUserQuarterData {
  id?: number
  note?: string
  assignee_id?: number
  sales_target: number
  sales_result: number
  is_rewarded?: boolean
  percent_reward?: number
  amount_reward: number
  _assignee: string
}

export interface ISalesQuarterData {
  id: number
  note: string
  quarter: string
  sales_target: number
  sales_result: number
  amount_reward: number
  sales_user_quarters: Array<ISalesUserQuarterData>
}

export const useSalesQuarterStore = defineStore('salesQuarter', {
  state: () => ({
    salesQuarter: {
      id: 0,
      note: '',
      quarter: '',
      sales_target: 0,
      sales_result: 0,
      amount_reward: 0,
      sales_user_quarters: [] as ISalesUserQuarterData[],
    } as ISalesQuarterData,
    isLoading: false,
  }),

  actions: {
    setSalesQuarter(data: ISalesQuarterData) {
      this.salesQuarter = data
    },

    updateSalesUserQuarterData(data: ISalesUserQuarterData) {
      const sales_user = this.salesQuarter.sales_user_quarters.find((s) => s.id === data.id)
      if (sales_user) {
        Object.assign(sales_user, data)
      } else {
        this.salesQuarter.sales_user_quarters.push(data)
      }
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getSalesQuarter(id: number) {
      try {
        this.setLoading(true)
        const { data } = await salesQuarterApi.get(id)
        this.setSalesQuarter(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateSalesUserQuarter(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await salesQuarterApi.updateSalesUserQuarter(payload.id, payload)
        this.updateSalesUserQuarterData(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateSalesQuarter(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await salesQuarterApi.update(this.salesQuarter.id, payload)
        this.setSalesQuarter(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applySalesQuarter() {
      try {
        this.setLoading(true)
        const { data } = await salesQuarterApi.apply(this.salesQuarter.id)
        this.setSalesQuarter(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectSalesQuarter(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await salesQuarterApi.reject(this.salesQuarter.id, payload)
        this.setSalesQuarter(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeSalesQuarter(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await salesQuarterApi.remake(this.salesQuarter.id, payload)
        this.setSalesQuarter(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveSalesQuarter() {
      try {
        this.setLoading(true)
        const { data } = await salesQuarterApi.approve(this.salesQuarter.id)
        this.setSalesQuarter(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useSalesQuarterListStore = defineStore('salesQuarterList', {
  state: () => ({
    count: 0,
    total: 0,
    salesQuarters: [] as ISalesQuarterData[],
    isLoading: false,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    setSalesQuarters(data: ISalesQuarterData[]) {
      this.salesQuarters = data
    },

    setTotal(value: number) {
      this.total = value
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async countSalesQuarters() {
      try {
        this.setLoading(true)
        const { data } = await salesQuarterApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listSalesQuarters(params: any) {
      try {
        this.setLoading(true)
        const result = await salesQuarterApi.list(params)
        const { data, total } = result as any
        this.setSalesQuarters(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllSalesQuarters(params: any) {
      try {
        this.setLoading(true)
        const result = await salesQuarterApi.listAll(params)
        const { data, total } = result as any
        this.setSalesQuarters(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
